import React from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import { debounce } from "lodash-es";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppInputLabel from "@toothfairy/shared-ui/AppInputLabel";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTrackSlider from "@toothfairy/shared-ui/AppTrackSlider";
import ToothFairySwitch from "../../../Components/ToothFairyComponents/ToothFairySwitch";
import Constants from "../../../Constants";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import useScreenSize from "../../../Hooks/useScreenSize";
import { isLargeScreen } from "../../MainViewContainer";

const TrainingSlider = ({ label, value, onSubmit, wrapperStyle, ...props }) => {
  const [localValue, setLocalValue] = React.useState(value);
  const onSubmitRef = React.useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const debouncedSubmit = React.useRef(
    debounce((v) => onSubmitRef.current(v), 600),
  ).current;

  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = React.useCallback(
    (v) => {
      setLocalValue(v);
      debouncedSubmit(v);
    },
    [debouncedSubmit],
  );

  return (
    <View style={[{ paddingHorizontal: 10, paddingBottom: 20 }, wrapperStyle]}>
      {label ? <AppInputLabel label={label} style={{ marginLeft: 0 }} /> : null}
      <AppTrackSlider
        value={localValue}
        onValueChange={handleChange}
        showValue
        {...props}
      />
    </View>
  );
};

const TrainingSettings = ({ workspace, handleWorkspaceUpdate }) => {
  const { t } = useTranslation();
  const {
    getActiveWorkspace,
    isPriorityTrainingEnabled,
    getPlanConfig,
  } = useWorkspaces();
  const { width } = useWindowDimensions();
  const { marginLeftUnderAppExpander } = useScreenSize();
  const { currentTheme } = AppTheme.useTheme();
  return (
    <View
      style={{
        zIndex: -1,
        alignSelf: "center",
        minWidth: 350,
      }}
    >
      {(getPlanConfig()?.isTrainingNLPEnabled ||
        getPlanConfig()?.isTrainingGNEnabled ||
        getPlanConfig()?.isTrainingQAEnabled) && (
        <AppExpander
          style={{ minWidth: isLargeScreen() ? 530 : 250 }}
          isExpandedOnLoad={true}
          title={t("settings.training.common")}
          icon={AppAssets.icons.settings}
        >
          <View
            style={{
              marginLeft: isLargeScreen() ? 5 : 0,
              maxWidth: 450,
              paddingLeft: 5,
            }}
          >
            <TrainingSlider
              step={1}
              maximumValue={360}
              minimumValue={10}
              label={t("settings.training.maxQueueTime")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  maxQueueWaitTime: v,
                })
              }
              value={
                workspace?.trainingConfig?.maxQueueWaitTime ||
                Constants.maxQueueWaitTime
              }
            />
            {/* <ToothFairySwitch
              wrapperStyle={{
                marginLeft: 0,
                marginTop: 20,
                maxWidth: !isLargeScreen() ? width - 60 : 385,
                backgroundColor: currentTheme?.theme?.inputBackgroundColor,
              }}
              labelColor="font_color"
              showTextInputLabel={true}
              disabled={!isPriorityTrainingEnabled()}
              label={"GPU scrap mode"}
              isTicked={
                !isPriorityTrainingEnabled()
                  ? true
                  : getActiveWorkspace()?.trainingConfig
                      ?.enableManagedSpotTraining
              }
              displayIsTicked={true}
              onChange={(v) => {
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  enableManagedSpotTraining: v,
                });
              }}
            /> */}
          </View>
        </AppExpander>
      )}

      {getPlanConfig()?.isTrainingGNEnabled && (
        <AppExpander
          style={{ minWidth: isLargeScreen() ? 530 : 250 }}
          isExpandedOnLoad={false}
          title={t("settings.training.generativeAI")}
          icon={AppAssets.icons.writer}
        >
          <View
            style={{
              marginLeft: isLargeScreen() ? 10 : 0,
              maxWidth: 450,
              paddingLeft: 5,
            }}
          >
            <TrainingSlider
              step={0.000001}
              maximumValue={0.01}
              minimumValue={0.000001}
              visualMultiplier={1000}
              label={t("settings.training.learningRate")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  learningRatGN: v,
                })
              }
              value={
                workspace?.trainingConfig?.learningRatGN ||
                Constants.learningRatGN
              }
            />
            <TrainingSlider
              step={1}
              maximumValue={5000}
              minimumValue={1}
              label={t("settings.training.minDatasetForTrainingTest")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  datasetTestSizeThresholdGN: v,
                })
              }
              value={
                workspace?.trainingConfig?.datasetTestSizeThresholdGN ||
                Constants.datasetTestSizeThresholdGN
              }
            />
            <TrainingSlider
              step={0.01}
              maximumValue={0.35}
              minimumValue={0.01}
              visualMultiplier={100}
              unitLabel="%"
              label={t("settings.training.testDatasetSize")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  testSizeGN: v,
                })
              }
              value={
                workspace?.trainingConfig?.testSizeGN || Constants.testSizeGN
              }
            />
            <TrainingSlider
              step={1}
              maximumValue={10}
              minimumValue={1}
              label={t("settings.training.maxEpochs")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  trainingEpochsGN: v,
                })
              }
              value={
                workspace?.trainingConfig?.trainingEpochsGN ||
                Constants.baseTrainingGNEpochs
              }
            />
          </View>
        </AppExpander>
      )}
      {getPlanConfig()?.isTrainingQAEnabled && (
        <AppExpander
          style={{ minWidth: isLargeScreen() ? 530 : 250 }}
          isExpandedOnLoad={true}
          title={t("settings.training.supervisedFinetuning")}
          icon={AppAssets.icons.answerer}
        >
          <View
            style={{
              marginLeft: isLargeScreen() ? 10 : 0,
              maxWidth: 450,
              paddingLeft: 0,
            }}
          >
            <TrainingSlider
              step={0.000001}
              maximumValue={0.0005}
              minimumValue={0.000001}
              visualMultiplier={1000}
              label={t("settings.training.learningRate")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  learningRateRC: v,
                })
              }
              value={
                workspace?.trainingConfig?.learningRateRC ||
                Constants.learningRateRC
              }
            />
            <TrainingSlider
              step={1}
              maximumValue={5000}
              minimumValue={1}
              label={t("settings.training.minDatasetForTrainingTest")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  datasetTestSizeThresholdRC: v,
                })
              }
              value={
                workspace?.trainingConfig?.datasetTestSizeThresholdRC ||
                Constants.datasetTestSizeThresholdRC
              }
            />
            <TrainingSlider
              step={0.01}
              maximumValue={0.35}
              minimumValue={0.01}
              visualMultiplier={100}
              unitLabel="%"
              label={t("settings.training.testDatasetSize")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  testSizeRC: v,
                })
              }
              value={
                workspace?.trainingConfig?.testSizeRC || Constants.testSizeRC
              }
            />
            <TrainingSlider
              step={1}
              maximumValue={100}
              minimumValue={1}
              label={t("settings.training.maxEpochs")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  trainingEpochsRC: v,
                })
              }
              value={
                workspace?.trainingConfig?.trainingEpochsRC ||
                Constants.baseTrainingRCEpochs
              }
            />

            {/* <ToothFairySwitch
            wrapperStyle={{
              marginLeft: 0,
              marginTop: 20,
              backgroundColor: currentTheme?.theme?.inputBackgroundColor,
              maxWidth: !isLargeScreen() ? width - 60 : 385,
            }}
            labelColor="font_color"
            showTextInputLabel={true}
            label={"Use RC as source"}
            isTicked={
              getActiveWorkspace()?.trainingConfig
                ?.includeReadComprehensionDataInTraining
            }
            displayIsTicked={true}
            onChange={(v) => {
              handleWorkspaceUpdate("trainingConfig", {
                ...getActiveWorkspace().trainingConfig,
                includeReadComprehensionDataInTraining: v,
              });
            }}
          /> */}
          </View>
        </AppExpander>
      )}
      {getPlanConfig()?.isTrainingNLPEnabled && (
        <AppExpander
          style={{ minWidth: isLargeScreen() ? 530 : 250 }}
          isExpandedOnLoad={false}
          title={t("settings.training.sentimentReviewer")}
          icon={AppAssets.icons.trainingLog}
        >
          <View
            style={{
              marginLeft: isLargeScreen() ? 10 : 0,
              maxWidth: 450,
              paddingLeft: 5,
            }}
          >
            <TrainingSlider
              step={0.000001}
              maximumValue={0.01}
              minimumValue={0.000001}
              visualMultiplier={1000}
              label={t("settings.training.learningRate")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  learningRateNLP: v,
                })
              }
              value={
                workspace?.trainingConfig?.learningRateNLP ||
                Constants.learningRateNLP
              }
            />
            <TrainingSlider
              step={1}
              maximumValue={5000}
              minimumValue={1}
              label={t("settings.training.minDatasetForTrainingTest")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  datasetTestSizeThresholdNLP: v,
                })
              }
              value={
                workspace?.trainingConfig?.datasetTestSizeThresholdNLP ||
                Constants.datasetTestSizeThresholdNLP
              }
            />
            <TrainingSlider
              step={0.01}
              maximumValue={0.35}
              minimumValue={0.01}
              visualMultiplier={100}
              unitLabel="%"
              label={t("settings.training.testDatasetSize")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  testSizeNLP: v,
                })
              }
              value={
                workspace?.trainingConfig?.testSizeNLP || Constants.testSizeNLP
              }
            />
            <TrainingSlider
              minimumValue={1}
              maximumValue={2000}
              step={10}
              label={t("settings.training.maxEpochs")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  trainingEpochsNLP: v,
                })
              }
              value={
                workspace?.trainingConfig?.trainingEpochsNLP ||
                Constants.baseTrainingNLPEpochs
              }
            />

            <TrainingSlider
              maximumValue={80000}
              minimumValue={20000}
              step={100}
              label={t("settings.training.maxSteps")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  maxStepsNLP: v,
                })
              }
              value={
                workspace?.trainingConfig?.maxStepsNLP || Constants.maxStepsNLP
              }
            />
            <TrainingSlider
              step={50}
              maximumValue={2400}
              minimumValue={800}
              label={t("settings.training.patience")}
              onSubmit={(v) =>
                handleWorkspaceUpdate("trainingConfig", {
                  ...getActiveWorkspace().trainingConfig,
                  patienceNLP: v,
                })
              }
              value={
                workspace?.trainingConfig?.patienceNLP || Constants.patienceNLP
              }
            />
          </View>
        </AppExpander>
      )}
      {!getPlanConfig()?.isTrainingNLPEnabled &&
        !getPlanConfig()?.isTrainingQAEnabled &&
        !getPlanConfig()?.isTrainingGNEnabled && (
          <AppPlaceholderView
            wrapperStyle={{
              width: "100%",
              height: "100%",
              marginVertical: 20,
              marginHorizontal: "auto",
              flex: 1,
            }}
            size={30}
            icon={AppAssets.icons.orbit}
            mainCaption="EmptyPlaceholder"
            subCaption="SettingsSubCaption"
          />
        )}
    </View>
  );
};

export default TrainingSettings;
